import React, { useRef, useState } from 'react';
import { FiPrinter } from 'react-icons/fi';
import styled from 'styled-components/macro';
import MenuIcon from 'svg/MenuIcon';
import LogoEleccion from 'svg/LogoEleccion';
import { useIsDesktopReal } from '../../hooks/useMediaQuery';
import { motion } from 'framer-motion';
import ReactDOM from 'react-dom';
import { useViewContext, useSideBarPortalRef } from 'contexts/viewContext';
import LinksList from './LinksList';
import { useNomenclator } from '../../contexts/nomenclatorContext';
import BuscadorSideBar from 'components/BuscadorSideBar';
import { NotificationButton } from '../../components/NotificationButton';
import useOutsideClick from '../../hooks/useOutsideClick';
import Expand from './Expand';
import RoundButton from '../Botones/RoundButton';
import { useTranslate } from '../../contexts/languageContext';
import CustomTooltip from '../CustomTooltip';
import { usePrintEvent } from '../../print/Print';
import useIni from '../../hooks/useIni';

const sideBarVariants = {
  index: {
    height: 'calc(90% - 2rem)',
  },
  results: {
    height: 'calc(98% - 2rem)',
  },
  estadisticas: {
    height: 'calc(98% - 2rem)',
  },
};

const expandVariants = {
  open: {
    maxWidth: '100%',
    transition: {
      duration: 0.5,
    },
  },
  closed: {
    maxWidth: '0%',
    transition: {
      delay: 0.5,
      duration: 0.5,
    },
  },
};

const visibiltyVariants = {
  visible: { opacity: 1 },
  hidden: { opacity: 0 },
};

const SideBar = ({ children }) => {
  const colorBarContainerRef = useRef(null);
  const isDesktop = useIsDesktopReal();
  const [open, setOpen] = useState(false);
  const containerRef = useOutsideClick(() => setOpen(false));
  const { view, esHistorico } = useViewContext();
  const sideBarPortal = useSideBarPortalRef();
  const t = useTranslate();
  const ini = useIni();
  const isClosed = ini?.isClosed;

  return isDesktop && !isClosed ? (
    <>
      <aside ref={containerRef}>
        <SidebarContainer>
          <Sidebar
            animate={[view, 'visible', open ? 'open' : 'closed']}
            variants={sideBarVariants}
            initial={['index', 'hidden', 'closed']}
            transition={{
              staggerChildren: 0.5,
            }}
          >
            <MenuButtonContainer>
              <CustomTooltip label={t('menu')}>
                <MenuButton
                  variants={visibiltyVariants}
                  onClick={() => setOpen(o => !o)}
                  disabled={esHistorico}
                >
                  <MenuIcon />
                </MenuButton>
              </CustomTooltip>
            </MenuButtonContainer>
            <Nav>
              <LinksList
                colorBarContainerRef={colorBarContainerRef}
                open={open}
                variant="small"
              />
            </Nav>
            <div>
              <Portal ref={sideBarPortal}>
                {
                  // Search bar
                  // Notifications button
                }
              </Portal>
              <ContainerLogo>
                <LogoEleccion width={75} />
              </ContainerLogo>
            </div>
          </Sidebar>
        </SidebarContainer>
        <SideBarColorBar ref={colorBarContainerRef} />
        <SideBarExpandContainer
          animate={[open ? 'open' : 'closed', view]}
          variants={expandVariants}
        >
          <SideBarExpand variants={sideBarVariants}>
            <Expand open={open} setOpen={setOpen} />
          </SideBarExpand>
        </SideBarExpandContainer>
      </aside>
      <LayoutContainer>{children}</LayoutContainer>
    </>
  ) : (
    children
  );
};

export const SideBarPortalRenderer = () => {
  const t = useTranslate();
  const sideBarPortal = useSideBarPortalRef();
  const nomenclator = useNomenclator();
  const printEvent = usePrintEvent();
  const { view } = useViewContext();

  return (
    <>
      {sideBarPortal?.current &&
        ReactDOM.createPortal(
          <div
            css={`
              display: flex;
              flex-direction: column;
              gap: 20px;
              padding: 20px 0px;
            `}
          >
            {(view === 'estadisticas' || view === 'results') && (
              <RoundButton label={t('imprimir')} onClick={() => printEvent()}>
                <FiPrinter style={{ width: '50%', height: '50%' }} />
              </RoundButton>
            )}
            <BuscadorSideBar />
            {/* <NotificationButton nomenclator={nomenclator} lateral={true} /> */}
          </div>,
          sideBarPortal.current
        )}
    </>
  );
};

const barWidth = '140px';
const colorBarWidth = '5px';

const SidebarContainer = styled.div`
  position: fixed;
  z-index: 2000;
  top: 0;
  left: 0;
  height: 100vh;
  width: ${barWidth};
  background-color: #d9d9d9;

  @media print {
    display: none;
  }
`;

const SideBarExpandContainer = styled(motion.div)`
  position: fixed;
  z-index: 1900;
  top: 0;
  left: calc(${barWidth} + ${colorBarWidth});
  height: 100vh;
  background-color: #ebeaea;
  overflow: hidden;
  @media print {
    display: none;
  }
`;

const SideBarExpand = styled(motion.div)`
  margin: 2rem 0;
  width: 500px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  background-color: #ebeaea;
`;

const Sidebar = styled(motion.div)`
  position: relative;
  margin: 2rem 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
`;

const SideBarColorBar = styled(motion.div)`
  height: 100vh;
  width: ${colorBarWidth};
  position: fixed;
  top: 0;
  left: ${barWidth};
  z-index: 1900;
  background-color: #d9d9d9;
  overflow-y: hidden;
  @media print {
    display: none;
  }
`;

const Nav = styled.nav`
  padding: 1rem 0rem 1rem 1rem;
  display: grid;
  overflow: hidden;
`;

const MenuButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const MenuButton = styled(motion.button)`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 60px;
  width: 60px;
  background-color: #215ccc;
  border-radius: 50%;
  color: white;

  &:hover {
    background: rgb(28, 40, 61, 0.5);
    color: white;
  }
  &:disabled {
    background: dimgrey;
    cursor: not-allowed;
  }
`;

const ContainerLogo = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
`;

const Portal = styled.div`
  margin: 2rem 0;
  display: flex;
  justify-content: center;
  align-items: center;
`;

const LayoutContainer = styled.div`
  padding-left: calc(${barWidth} + ${colorBarWidth});
  @media print {
    padding-left: 0;
  }
`;

export default SideBar;
